import React, { useEffect, useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import * as _ from 'lodash';
import PropTypes from 'prop-types';
import {getUserData, redirectToTenant} from '../actions';
import ErrorPage from '../components/error/ErrorPage';
import { getRedirectTenantInfo } from './utils';
import {
    invitationList,
    userTenantDetailsList,
    ociErrorInfo,
    loading,
    userData
} from './selectors';
import {
    DisabledStandaloneUiModeErrorMessage,
} from '../components/error/ErrorMessagesComps';

import Callback from '../components/Callback';

//Guarantee always run once before rendering
const BeforeFirstRender = ( func, params ) => {
    const willLoad = useRef(true);
    if (willLoad.current) {
        if (typeof func === 'function') {
            func(params);
        }
    }
    useEffect(() => {
        willLoad.current = false;
    });
};

export function CigServLoadingPage({ auth, tenants, invitations, errorInfo, redirectToTenant, isLoading, getUserData }) {
    const [error, setError] = useState(false);
    const [hasInvitation, setHasInvitation] = useState(false);

    BeforeFirstRender(getUserData, auth);

    /**
     * Redirect to Gateway --> Redirect to Tenant
     * If the service endpoint is /service, no mode query param is added.
     * If the service endpoint is /service/cm
     *     - If mode is existed from query parameter, add mode to redirect path
     *     - If mode does not exist or empty and uiMode is not NULL, add uiMode to redirect path
     *     - If mode does not exist or empty and uiMode is empty or undefined or null, use default mode which is "CM"
     *
     * @type {(function(*, *, *): void)|*}
     */
    const redirectToTenantFn = useCallback((tenantId, hostName) => {
        let redirectRelPath = '/web/#';
        redirectToTenant(window, auth, tenantId, hostName, redirectRelPath);
    },[auth, redirectToTenant]);

    useEffect(() => {
        if (!_.isEmpty(tenants) && tenants.length > 0 && _.isEmpty(errorInfo.errorMessage)) {
            const { errorStatus, tenantId, hostName, status, uiMode } = getRedirectTenantInfo({tenants});
            if (errorStatus) {
                setError(errorStatus);
            }
            else if (status === 'RUNNING') {
                redirectToTenantFn(tenantId, hostName, uiMode);
            }
            else if (_.startsWith(status, 'CREATING')) {
                setError(true)
            }
        }
        else if (!_.isUndefined(invitations) && invitations.length > 0)  {
            if (invitations[0]) {
                setHasInvitation(true);
            }
        }
        else  {
            setError(true);
        }
    }, [ tenants, invitations, auth, redirectToTenantFn, errorInfo ]);

    const renderError = () => {
        if (error) {
            const ociSite = _.isEmpty(tenants) ? '' : _.get(tenants[0], 'hostname', '');
            if (_.isEmpty(ociSite)) {
                return <ErrorPage
                    id="standalone-ui-disabled-error"
                    title={'Service Path Unavailable'}
                    showCIContact={true}
                    auth={auth}
                    serverMsg={'Tried to create standalone tenant from /service endpoint with standaloneUiMode is: ' + window.CIG.standaloneMode} logPrefix='CigServLoadingPage:'
                    data-testid="tandalone-ui-disabled-error">

                    <DisabledStandaloneUiModeErrorMessage />
                </ErrorPage>
            }
            else {
                const tenantStatus = _.get(tenants[0], 'status', '');
                if (_.startsWith(tenantStatus, 'CREATING')) {
                    return <ErrorPage id="tenant-status-creating"
                        title={'Your Data Infrastructure Insights tenant is being created. Please check back later.'}
                        message={`Data Infrastructure Insights: ${ociSite}`} showCIContact={true}
                        auth={auth}
                        serverMsg={`Tenant is in ${tenantStatus} status.`} logPrefix='CigServLoadingPage:'
                        data-testid="tenant-status-creating"
                    />
                }
                else if (_.startsWith(tenantStatus, 'FAIL')) {
                    return <ErrorPage id="tenant-status-error"
                        title={'We encountered an error while accessing your Data Infrastructure Insights tenant.'}
                        message={`Data Infrastructure Insights: ${ociSite}`} showCIContact={true}
                        auth={auth}
                        serverMsg={`Tenant: ${ociSite}, tenant status: ${tenantStatus}`} logPrefix='CigServLoadingPage:'
                        data-testid="tenant-status-error"
                    />
                }
            }
        }
        else if (!_.isEmpty(_.get(errorInfo, 'errorMessage', ''))) {
            return <ErrorPage id="error-from-server" auth={auth} message={errorInfo.errorMessage}
                showCIContact={true} serverMsg={errorInfo.errorMessage}
                errorCode={errorInfo.errorCode} httpStatusCode={errorInfo.httpStatusCode}
                logPrefix='CigServLoadingPage:'
                data-testid="error-from-server"/>
        }
    };

    if (isLoading) {
        return (
            <Callback />
        );
    }

    return (
        <div id="service-loading-page" data-testid="service-loading-page">
            { renderError() }
            {
                hasInvitation && (
                    <Redirect id="redirect-invite" to={{
                        pathname: '/acceptInvite',
                        search: '?context=' + invitations[0].id
                    }} data-testid="redirect-invite"/>
                )
            }
        </div>
    );
}

CigServLoadingPage.propTypes  = {
    auth: PropTypes.object,
    errorInfo: PropTypes.object,
    userData: PropTypes.object,
    isLoading: PropTypes.bool.isRequired,
    invitations: PropTypes.array,
    tenants: PropTypes.array,
    getUserData: PropTypes.func,
    redirectToTenant: PropTypes.func.isRequired,
    getRedirectTenantInfo: PropTypes.func,
};


CigServLoadingPage.displayName = 'CigServLoadingPage';
function mapStateToProps(state){
    return {
        invitations: invitationList(state),
        tenants: userTenantDetailsList(state),
        errorInfo: ociErrorInfo(state),
        isLoading: loading(state),
        userData: userData(state),
    }
}
export default withRouter(
    connect(
        mapStateToProps,
        {
            redirectToTenant,
            getUserData,
        }
    )(CigServLoadingPage));
